import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MapPin, Loader2, Crosshair, PenTool, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const LocationPickerWithBoundary = ({ 
  latitude, 
  longitude, 
  boundary,
  onLocationChange, 
  onBoundaryChange,
  onAreaCalculated,
  height = '400px' 
}) => {
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const polygonLayerRef = useRef(null);
  const drawControlRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [leafletModule, setLeafletModule] = useState(null);

  // Default center on France
  const defaultCenter = [46.603354, 1.888334];
  const defaultZoom = 6;

  // Calculate area from polygon coordinates (in hectares)
  const calculateArea = useCallback((latlngs) => {
    if (!leafletModule || !latlngs || latlngs.length < 3) return 0;
    
    const L = leafletModule;
    // Use Leaflet's geodesic area calculation
    const polygon = L.polygon(latlngs);
    const areaInMeters = L.GeometryUtil ? L.GeometryUtil.geodesicArea(latlngs) : 0;
    
    // Simple spherical excess formula for area calculation
    if (!areaInMeters) {
      let area = 0;
      const coords = latlngs.map(ll => [ll.lat || ll[0], ll.lng || ll[1]]);
      const n = coords.length;
      
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += coords[i][1] * coords[j][0];
        area -= coords[j][1] * coords[i][0];
      }
      
      area = Math.abs(area) / 2;
      // Convert to approximate square meters (rough approximation at French latitudes)
      const metersPerDegree = 111320; // at equator
      const latFactor = Math.cos((coords[0][0] * Math.PI) / 180);
      const areaM2 = area * metersPerDegree * metersPerDegree * latFactor;
      return areaM2 / 10000; // Convert to hectares
    }
    
    return areaInMeters / 10000; // Convert to hectares
  }, [leafletModule]);

  // Initialize map
  useEffect(() => {
    let mounted = true;

    const initMap = async () => {
      if (!containerRef.current || mapInstanceRef.current) return;

      try {
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');
        await import('leaflet-draw');
        await import('leaflet-draw/dist/leaflet.draw.css');

        if (!mounted || !containerRef.current) return;

        setLeafletModule(L.default);

        // Fix default icons
        delete L.default.Icon.Default.prototype._getIconUrl;
        L.default.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        // Determine initial center
        const initialCenter = (latitude && longitude) 
          ? [latitude, longitude] 
          : defaultCenter;
        const initialZoom = (latitude && longitude) ? 12 : defaultZoom;

        // Create map
        const map = L.default.map(containerRef.current).setView(initialCenter, initialZoom);
        mapInstanceRef.current = map;

        // Add tile layer
        L.default.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap'
        }).addTo(map);

        // Create feature group for drawn items
        const drawnItems = new L.default.FeatureGroup();
        map.addLayer(drawnItems);
        polygonLayerRef.current = drawnItems;

        // Add existing boundary if present
        if (boundary && boundary.length >= 3) {
          const polygon = L.default.polygon(boundary, {
            color: '#0F5338',
            fillColor: '#0F5338',
            fillOpacity: 0.2,
            weight: 3,
          });
          drawnItems.addLayer(polygon);
          map.fitBounds(polygon.getBounds(), { padding: [50, 50] });
        }

        // Create custom icon for picker
        const pickerIcon = L.default.divIcon({
          className: 'location-picker-marker',
          html: `<div style="
            background-color: #0F5338;
            width: 36px;
            height: 36px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 2px 10px rgba(0,0,0,0.4);
            cursor: move;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(45deg);">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>`,
          iconSize: [36, 36],
          iconAnchor: [18, 36],
        });

        // Add marker if we have coordinates
        if (latitude && longitude) {
          const marker = L.default.marker([latitude, longitude], { 
            icon: pickerIcon,
            draggable: true 
          }).addTo(map);
          
          marker.on('dragend', (e) => {
            const pos = e.target.getLatLng();
            onLocationChange(pos.lat, pos.lng);
          });
          
          markerRef.current = marker;
        }

        // Handle map clicks for marker placement
        map.on('click', (e) => {
          if (isDrawing) return;
          
          const { lat, lng } = e.latlng;
          
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          } else {
            const marker = L.default.marker([lat, lng], { 
              icon: pickerIcon,
              draggable: true 
            }).addTo(map);
            
            marker.on('dragend', (ev) => {
              const pos = ev.target.getLatLng();
              onLocationChange(pos.lat, pos.lng);
            });
            
            markerRef.current = marker;
          }
          
          onLocationChange(lat, lng);
        });

        // Setup draw control
        const drawControl = new L.default.Control.Draw({
          position: 'topright',
          draw: {
            polygon: {
              allowIntersection: false,
              drawError: {
                color: '#e1e1e1',
                message: '<strong>Erreur:</strong> Les lignes ne peuvent pas se croiser!'
              },
              shapeOptions: {
                color: '#0F5338',
                fillColor: '#0F5338',
                fillOpacity: 0.2,
                weight: 3,
              }
            },
            polyline: false,
            circle: false,
            rectangle: false,
            marker: false,
            circlemarker: false,
          },
          edit: {
            featureGroup: drawnItems,
            remove: true,
            edit: true,
          }
        });
        
        map.addControl(drawControl);
        drawControlRef.current = drawControl;

        // Handle draw events
        map.on(L.default.Draw.Event.CREATED, (e) => {
          const layer = e.layer;
          
          // Clear existing polygons
          drawnItems.eachLayer((l) => {
            if (l instanceof L.default.Polygon) {
              drawnItems.removeLayer(l);
            }
          });
          
          drawnItems.addLayer(layer);
          
          const latlngs = layer.getLatLngs()[0];
          const coords = latlngs.map(ll => [ll.lat, ll.lng]);
          onBoundaryChange(coords);
          
          // Calculate and set area
          const area = calculateArea(latlngs);
          if (onAreaCalculated && area > 0) {
            onAreaCalculated(area);
          }
          
          // Set center point
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          onLocationChange(center.lat, center.lng);
          
          setIsDrawing(false);
        });

        map.on(L.default.Draw.Event.EDITED, (e) => {
          e.layers.eachLayer((layer) => {
            if (layer instanceof L.default.Polygon) {
              const latlngs = layer.getLatLngs()[0];
              const coords = latlngs.map(ll => [ll.lat, ll.lng]);
              onBoundaryChange(coords);
              
              const area = calculateArea(latlngs);
              if (onAreaCalculated && area > 0) {
                onAreaCalculated(area);
              }
              
              const bounds = layer.getBounds();
              const center = bounds.getCenter();
              onLocationChange(center.lat, center.lng);
            }
          });
        });

        map.on(L.default.Draw.Event.DELETED, () => {
          onBoundaryChange([]);
          if (onAreaCalculated) {
            onAreaCalculated(0);
          }
        });

        map.on(L.default.Draw.Event.DRAWSTART, () => {
          setIsDrawing(true);
        });

        map.on(L.default.Draw.Event.DRAWSTOP, () => {
          setIsDrawing(false);
        });

        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize location picker:', error);
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        polygonLayerRef.current = null;
      }
    };
  }, []);

  // Start drawing polygon
  const startDrawing = useCallback(() => {
    if (!mapInstanceRef.current || !leafletModule) return;
    
    const map = mapInstanceRef.current;
    const L = leafletModule;
    
    // Trigger polygon drawing
    new L.Draw.Polygon(map, {
      shapeOptions: {
        color: '#0F5338',
        fillColor: '#0F5338',
        fillOpacity: 0.2,
        weight: 3,
      }
    }).enable();
    
    setIsDrawing(true);
  }, [leafletModule]);

  // Clear boundary
  const clearBoundary = useCallback(() => {
    if (!polygonLayerRef.current) return;
    
    polygonLayerRef.current.eachLayer((layer) => {
      polygonLayerRef.current.removeLayer(layer);
    });
    
    onBoundaryChange([]);
    if (onAreaCalculated) {
      onAreaCalculated(0);
    }
  }, [onBoundaryChange, onAreaCalculated]);

  return (
    <div className="space-y-3">
      {/* Drawing controls */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={isDrawing ? "default" : "outline"}
          size="sm"
          onClick={startDrawing}
          disabled={!isReady}
          data-testid="draw-boundary-btn"
        >
          <PenTool className="h-4 w-4 mr-2" />
          {isDrawing ? 'Tracé en cours...' : 'Tracer les limites'}
        </Button>
        {boundary && boundary.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearBoundary}
            data-testid="clear-boundary-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Effacer le tracé
          </Button>
        )}
      </div>

      <div 
        className="relative rounded-xl overflow-hidden border bg-card shadow-sm"
        style={{ height }}
        data-testid="location-picker-boundary"
      >
        <div 
          ref={containerRef} 
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        />
        
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-card">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Chargement de la carte...</p>
            </div>
          </div>
        )}

        {/* Instructions overlay */}
        {isReady && !boundary?.length && !isDrawing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border">
            <div className="flex items-center gap-2 text-sm">
              <Crosshair className="h-4 w-4 text-primary" />
              <span className="text-slate-600 dark:text-slate-300">
                Cliquez pour placer le centre ou tracez les limites
              </span>
            </div>
          </div>
        )}

        {/* Drawing mode indicator */}
        {isDrawing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-primary text-primary-foreground rounded-lg px-4 py-2 shadow-lg">
            <div className="flex items-center gap-2 text-sm font-medium">
              <PenTool className="h-4 w-4 animate-pulse" />
              <span>Cliquez pour tracer les limites, double-cliquez pour terminer</span>
            </div>
          </div>
        )}

        {/* Coordinates and area display */}
        {isReady && (latitude || boundary?.length > 0) && (
          <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border">
            <div className="space-y-1">
              {latitude && longitude && (
                <div className="flex items-center gap-2 text-xs">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  <span className="font-mono text-slate-600 dark:text-slate-300">
                    {latitude.toFixed(5)}, {longitude.toFixed(5)}
                  </span>
                </div>
              )}
              {boundary?.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <PenTool className="h-3.5 w-3.5 text-primary" />
                  <span className="text-slate-600 dark:text-slate-300">
                    Limites tracées ({boundary.length} points)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground">
        Utilisez le bouton "Tracer les limites" pour dessiner le périmètre de l'exploitation. La surface sera calculée automatiquement.
      </p>
    </div>
  );
};

export default LocationPickerWithBoundary;
